import { useState, useRef, useEffect } from "react";
import { useRoute } from "wouter";
import { Upload, FileText, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface RequestInfo {
  requestId: string;
  title: string;
  notes: string | null;
  folder_path: string;
  accountName: string | null;
  expiresAt: string | null;
}

type PageState = "loading" | "ready" | "uploading" | "done" | "error" | "gone";

export default function UploadPage() {
  const [, params] = useRoute("/upload/:token");
  const token = params?.token ?? "";

  const [state, setState] = useState<PageState>("loading");
  const [info, setInfo] = useState<RequestInfo | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<"idle" | "presigning" | "uploading" | "recording">("idle");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) { setState("error"); setErrorMsg("Missing upload token."); return; }
    fetch(`/api/upload/${token}`)
      .then(async (r) => {
        if (r.status === 404) throw new Error("This upload link is invalid. It may have been revoked.");
        if (r.status === 410) {
          const body = await r.json().catch(() => ({}));
          throw Object.assign(new Error(body.message || "This link is no longer active."), { gone: true });
        }
        if (!r.ok) throw new Error("Failed to load request details.");
        return r.json();
      })
      .then((data: RequestInfo) => {
        setInfo(data);
        setState("ready");
      })
      .catch((err) => {
        setErrorMsg(err.message);
        setState(err.gone ? "gone" : "error");
      });
  }, [token]);

  async function uploadFile(file: File) {
    setState("uploading");
    setSelectedFile(file);
    try {
      // 1. Get presigned PUT URL
      setProgress("presigning");
      const presignRes = await fetch(`/api/upload/${token}/presign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileName: file.name, contentType: file.type || "application/octet-stream", fileSize: file.size }),
      });
      if (!presignRes.ok) {
        const body = await presignRes.json().catch(() => ({}));
        throw new Error(body.message || "Failed to prepare upload.");
      }
      const { uploadUrl, intentId } = await presignRes.json();

      // 2. Upload directly to R2
      setProgress("uploading");
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });
      if (!putRes.ok) throw new Error("File upload failed. Please try again.");

      // 3. Record the upload
      setProgress("recording");
      const completeRes = await fetch(`/api/upload/${token}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intentId }),
      });
      if (!completeRes.ok) {
        const body = await completeRes.json().catch(() => ({}));
        throw new Error(body.message || "Upload recorded but confirmation failed.");
      }

      setState("done");
    } catch (err: any) {
      setErrorMsg(err.message || "An unexpected error occurred.");
      setState("error");
    } finally {
      setProgress("idle");
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  }

  const progressLabel: Record<typeof progress, string> = {
    idle: "Uploading…",
    presigning: "Preparing upload…",
    uploading: "Uploading to secure storage…",
    recording: "Confirming…",
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
      {/* Branding strip */}
      <div className="w-full max-w-md mb-6 text-center">
        <p className="text-sm font-semibold text-slate-700 tracking-wide">Carpenter &amp; Company, CPAs, PC</p>
      </div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100">
          <h1 className="text-lg font-semibold text-slate-800">Secure Document Upload</h1>
          {info?.accountName && (
            <p className="text-sm text-muted-foreground mt-0.5">{info.accountName}</p>
          )}
        </div>

        <div className="px-6 py-5">
          {/* Loading */}
          {state === "loading" && (
            <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading request…</span>
            </div>
          )}

          {/* Gone (fulfilled/cancelled/expired) */}
          {state === "gone" && (
            <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
              <AlertCircle className="w-10 h-10 text-amber-400" />
              <p className="text-sm font-medium text-slate-700">{errorMsg}</p>
              <p className="text-xs text-muted-foreground">
                If you need to submit a document, please contact your accountant.
              </p>
            </div>
          )}

          {/* Error */}
          {state === "error" && (
            <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
              <AlertCircle className="w-10 h-10 text-red-400" />
              <p className="text-sm font-medium text-slate-700">{errorMsg}</p>
              <p className="text-xs text-muted-foreground">
                Please contact your accountant if this problem persists.
              </p>
            </div>
          )}

          {/* Ready — show request info + drop zone */}
          {state === "ready" && info && (
            <div className="flex flex-col gap-4">
              {/* Request details */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Requested document</p>
                <p className="text-sm font-semibold text-slate-800">{info.title}</p>
                {info.notes && <p className="text-xs text-muted-foreground mt-1">{info.notes}</p>}
              </div>

              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
                className={cn(
                  "flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed cursor-pointer transition-colors py-10 px-4",
                  dragOver
                    ? "border-primary bg-primary/5"
                    : "border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                )}
              >
                <Upload className="w-8 h-8 text-slate-400" />
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-700">
                    {dragOver ? "Drop to upload" : "Click or drag a file here"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    PDF, Word, Excel, images — any file type accepted
                  </p>
                </div>
              </div>
              <input
                ref={inputRef}
                type="file"
                className="hidden"
                onChange={handleFileInput}
              />

              <Button
                className="w-full gap-2"
                onClick={() => inputRef.current?.click()}
              >
                <Upload className="w-4 h-4" />
                Choose File
              </Button>

              {info.expiresAt && (
                <p className="text-center text-[11px] text-muted-foreground">
                  Link expires {new Date(info.expiresAt).toLocaleDateString("en-US", {
                    month: "long", day: "numeric", year: "numeric",
                  })}
                </p>
              )}
            </div>
          )}

          {/* Uploading */}
          {state === "uploading" && (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
              <div className="text-center">
                <p className="text-sm font-medium text-slate-700">{progressLabel[progress]}</p>
                {selectedFile && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1">
                    <FileText className="w-3.5 h-3.5" />
                    {selectedFile.name}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Done */}
          {state === "done" && (
            <div className="flex flex-col items-center justify-center py-10 gap-4 text-center">
              <CheckCircle className="w-12 h-12 text-green-500" />
              <div>
                <p className="text-base font-semibold text-slate-800">Upload complete!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Your document has been securely delivered to Carpenter &amp; Company.
                </p>
                {selectedFile && (
                  <p className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1">
                    <FileText className="w-3 h-3" />
                    {selectedFile.name}
                  </p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">You may close this tab.</p>
            </div>
          )}
        </div>
      </div>

      <p className="mt-6 text-xs text-slate-400">
        Files are encrypted and stored securely. This link is single-use.
      </p>
    </div>
  );
}
